import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { supabase } from "@/integrations/supabase/client";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent } from "@/components/ui/tabs";
import { PillTabsList, PillTabsTrigger } from "@/components/admin/pill-tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { AdminPageHeader, RowActions } from "@/components/admin/crud-helpers";
import { reviewTopupRequest } from "@/lib/topups.functions";
import {
  Check, X, Clock, CreditCard, Banknote, Bitcoin, Smartphone, Wallet, Inbox, Search, Filter, TrendingUp,
} from "lucide-react";
import { StatsStrip } from "@/components/admin/stats-strip";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/admin/topups")({
  component: TopupsPage,
});

type MethodKind = "card" | "bank" | "crypto" | "mobile_money" | "other";
type TopupMethod = {
  id: string;
  name: string;
  kind: MethodKind;
  currency: string;
  min_amount_cents: number;
  max_amount_cents: number;
  fee_percent: number;
  fee_flat_cents: number;
  instructions: string | null;
  account_details: Record<string, unknown>;
  auto_credit: boolean;
  enabled: boolean;
  sort_order: number;
};

type TopupRequest = {
  id: string;
  user_id: string;
  method_id: string | null;
  amount_cents: number;
  fee_cents: number;
  credit_cents: number;
  currency: string;
  status: "pending" | "approved" | "rejected" | "cancelled";
  reference: string | null;
  proof_url: string | null;
  notes: string | null;
  created_at: string;
  reviewed_at: string | null;
  rejection_reason: string | null;
  profile?: { display_name: string | null } | null;
  method?: { name: string; kind: MethodKind } | null;
};

const KIND_META: Record<MethodKind, { label: string; icon: React.ReactNode }> = {
  card: { label: "Card", icon: <CreditCard className="h-4 w-4" /> },
  bank: { label: "Bank transfer", icon: <Banknote className="h-4 w-4" /> },
  crypto: { label: "Crypto", icon: <Bitcoin className="h-4 w-4" /> },
  mobile_money: { label: "Mobile money", icon: <Smartphone className="h-4 w-4" /> },
  other: { label: "Other", icon: <Wallet className="h-4 w-4" /> },
};

function fmt(cents: number, currency = "USD") {
  return new Intl.NumberFormat("en-US", { style: "currency", currency }).format(cents / 100);
}

function TopupsPage() {
  const qc = useQueryClient();
  const [editing, setEditing] = useState<Partial<TopupMethod> | null>(null);

  const methods = useQuery({
    queryKey: ["admin-topup-methods"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("topup_methods")
        .select("*")
        .order("sort_order")
        .order("name");
      if (error) throw error;
      return (data ?? []) as TopupMethod[];
    },
  });

  const requests = useQuery({
    queryKey: ["admin-topup-requests"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("topup_requests")
        .select("*, method:topup_methods(name,kind)")
        .order("created_at", { ascending: false })
        .limit(200);
      if (error) throw error;
      const ids = Array.from(new Set((data ?? []).map((r) => r.user_id)));
      let profiles: Record<string, { display_name: string | null }> = {};
      if (ids.length) {
        const { data: profs } = await supabase
          .from("profiles")
          .select("id,display_name")
          .in("id", ids);
        profiles = Object.fromEntries((profs ?? []).map((p) => [p.id, p]));
      }
      return (data ?? []).map((r) => ({ ...r, profile: profiles[r.user_id] ?? null })) as TopupRequest[];
    },
  });

  const stats = useMemo(() => {
    const r = requests.data ?? [];
    const pending = r.filter((x) => x.status === "pending");
    const approved = r.filter((x) => x.status === "approved");
    const rejected = r.filter((x) => x.status === "rejected");
    const approvedTotal = approved.reduce((s, x) => s + x.credit_cents, 0);
    return { total: r.length, pending: pending.length, approved: approved.length, rejected: rejected.length, approvedTotal };
  }, [requests.data]);
  const pendingCount = stats.pending;

  const saveMethod = useMutation({
    mutationFn: async (m: Partial<TopupMethod>) => {
      const payload: Record<string, unknown> = {
        name: m.name?.trim(),
        kind: m.kind,
        currency: m.currency || "USD",
        min_amount_cents: m.min_amount_cents ?? 100,
        max_amount_cents: m.max_amount_cents ?? 1_000_000,
        fee_percent: m.fee_percent ?? 0,
        fee_flat_cents: m.fee_flat_cents ?? 0,
        instructions: m.instructions || null,
        account_details: m.account_details ?? {},
        auto_credit: m.auto_credit ?? false,
        enabled: m.enabled ?? true,
        sort_order: m.sort_order ?? 0,
      };
      if (!payload.name || !payload.kind) throw new Error("Name and kind required");
      if (m.id) {
        const { error } = await supabase.from("topup_methods").update(payload as never).eq("id", m.id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from("topup_methods").insert(payload as never);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      toast.success("Saved");
      setEditing(null);
      qc.invalidateQueries({ queryKey: ["admin-topup-methods"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const deleteMethod = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("topup_methods").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Deleted");
      qc.invalidateQueries({ queryKey: ["admin-topup-methods"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const toggleEnabled = useMutation({
    mutationFn: async ({ id, enabled }: { id: string; enabled: boolean }) => {
      const { error } = await supabase.from("topup_methods").update({ enabled }).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin-topup-methods"] }),
  });

  return (
    <div className="space-y-6">
      <AdminPageHeader
        title="Top-ups & methods"
        description="Configure top-up methods and review user top-up requests."
        onNew={() =>
          setEditing({
            name: "",
            kind: "bank",
            currency: "USD",
            min_amount_cents: 500,
            max_amount_cents: 1_000_000,
            fee_percent: 0,
            fee_flat_cents: 0,
            instructions: "",
            account_details: {},
            auto_credit: false,
            enabled: true,
            sort_order: 0,
          })
        }
        newLabel="New method"
      />

      <StatsStrip
        tiles={[
          { label: "Total requests", value: stats.total, icon: Inbox },
          { label: "Pending", value: stats.pending, icon: Clock, tone: "warning" },
          { label: "Approved", value: stats.approved, icon: Check, tone: "success" },
          { label: "Rejected", value: stats.rejected, icon: X, tone: "danger" },
          { label: "Credited", value: fmt(stats.approvedTotal), icon: TrendingUp, tone: "info" },
        ]}
      />

      <Tabs defaultValue="requests">
        <PillTabsList search>
          <PillTabsTrigger value="requests">
            Requests{pendingCount > 0 && (
              <Badge variant="destructive" className="ml-2">{pendingCount}</Badge>
            )}
          </PillTabsTrigger>
          <PillTabsTrigger value="methods">Methods</PillTabsTrigger>
        </PillTabsList>

        <TabsContent value="requests">
          <RequestsTable
            rows={requests.data ?? []}
            loading={requests.isLoading}
            onReviewed={() => {
              qc.invalidateQueries({ queryKey: ["admin-topup-requests"] });
              qc.invalidateQueries({ queryKey: ["admin-wallets"] });
              qc.invalidateQueries({ queryKey: ["admin-wallet-txns"] });
            }}
          />
        </TabsContent>

        <TabsContent value="methods" className="space-y-3">
          <div className="grid gap-3 md:grid-cols-2">
            {(methods.data ?? []).map((m) => (
              <Card key={m.id}>
                <CardHeader className="flex flex-row items-start justify-between gap-2 pb-2">
                  <div>
                    <CardTitle className="flex items-center gap-2 text-base">
                      {KIND_META[m.kind].icon} {m.name}
                    </CardTitle>
                    <div className="mt-1 flex gap-2 text-xs text-muted-foreground">
                      <Badge variant="secondary">{KIND_META[m.kind].label}</Badge>
                      <Badge variant="outline">{m.currency}</Badge>
                      {m.auto_credit && <Badge>Auto-credit</Badge>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={m.enabled}
                      onCheckedChange={(v) => toggleEnabled.mutate({ id: m.id, enabled: v })}
                    />
                    <RowActions
                      onEdit={() => setEditing(m)}
                      onDelete={() => {
                        if (confirm(`Delete "${m.name}"?`)) deleteMethod.mutate(m.id);
                      }}
                    />
                  </div>
                </CardHeader>
                <CardContent className="space-y-1 text-sm text-muted-foreground">
                  <div>Limits: {fmt(m.min_amount_cents, m.currency)} – {fmt(m.max_amount_cents, m.currency)}</div>
                  <div>
                    Fee: {Number(m.fee_percent)}% {m.fee_flat_cents > 0 && `+ ${fmt(m.fee_flat_cents, m.currency)}`}
                  </div>
                  {m.instructions && <div className="line-clamp-2">{m.instructions}</div>}
                </CardContent>
              </Card>
            ))}
            {(methods.data ?? []).length === 0 && !methods.isLoading && (
              <Card>
                <CardContent className="py-10 text-center text-sm text-muted-foreground">
                  No top-up methods yet. Add one to let users fund their wallet.
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>
      </Tabs>

      <MethodEditor
        value={editing}
        onClose={() => setEditing(null)}
        onSave={(v) => saveMethod.mutate(v)}
        saving={saveMethod.isPending}
      />
    </div>
  );
}

function RequestsTable({
  rows,
  loading,
  onReviewed,
}: {
  rows: TopupRequest[];
  loading: boolean;
  onReviewed: () => void;
}) {
  const review = useServerFn(reviewTopupRequest);
  const [busy, setBusy] = useState<string | null>(null);
  const [rejectFor, setRejectFor] = useState<TopupRequest | null>(null);
  const [reason, setReason] = useState("");
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<"all" | TopupRequest["status"]>("all");

  const act = async (id: string, action: "approve" | "reject", r?: string) => {
    setBusy(id);
    try {
      await review({ data: { request_id: id, action, reason: r } });
      toast.success(action === "approve" ? "Approved & credited" : "Rejected");
      onReviewed();
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setBusy(null);
    }
  };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return rows.filter((r) => {
      if (statusFilter !== "all" && r.status !== statusFilter) return false;
      if (!q) return true;
      return (
        (r.profile?.display_name ?? "").toLowerCase().includes(q) ||
        r.user_id.toLowerCase().includes(q) ||
        (r.reference ?? "").toLowerCase().includes(q) ||
        (r.method?.name ?? "").toLowerCase().includes(q)
      );
    });
  }, [rows, query, statusFilter]);

  const filterChips: { value: typeof statusFilter; label: string }[] = [
    { value: "all", label: "All" },
    { value: "pending", label: "Pending" },
    { value: "approved", label: "Approved" },
    { value: "rejected", label: "Rejected" },
    { value: "cancelled", label: "Cancelled" },
  ];

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative min-w-0 flex-1 sm:max-w-xs">
          <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search user, reference, method…"
            className="pl-8"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
        <div className="flex flex-wrap items-center gap-1">
          <Filter className="h-3.5 w-3.5 text-muted-foreground" />
          {filterChips.map((c) => (
            <Button
              key={c.value}
              size="sm"
              variant={statusFilter === c.value ? "default" : "outline"}
              className="h-7 rounded-full px-3 text-xs"
              onClick={() => setStatusFilter(c.value)}
            >
              {c.label}
            </Button>
          ))}
        </div>
      </div>

    <Card>
      <CardContent className="p-0 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="border-b bg-muted/40 text-left text-xs uppercase text-muted-foreground">
            <tr>
              <th className="px-3 py-2">When</th>
              <th className="px-3 py-2">User</th>
              <th className="px-3 py-2">Method</th>
              <th className="px-3 py-2">Amount</th>
              <th className="px-3 py-2">Credit</th>
              <th className="px-3 py-2">Reference</th>
              <th className="px-3 py-2">Status</th>
              <th className="px-3 py-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((r) => (
              <tr key={r.id} className="border-b last:border-0 align-top hover:bg-muted/30">
                <td className="px-3 py-2 text-muted-foreground whitespace-nowrap">
                  {new Date(r.created_at).toLocaleString()}
                </td>
                <td className="px-3 py-2">
                  <div>{r.profile?.display_name ?? "—"}</div>
                  <div className="font-mono text-xs text-muted-foreground">{r.user_id.slice(0, 8)}…</div>
                </td>
                <td className="px-3 py-2">{r.method?.name ?? "—"}</td>
                <td className="px-3 py-2 tabular-nums">{fmt(r.amount_cents, r.currency)}</td>
                <td className="px-3 py-2 tabular-nums font-medium">{fmt(r.credit_cents, r.currency)}</td>
                <td className="px-3 py-2 text-xs">
                  {r.reference || "—"}
                  {r.proof_url && (
                    <div>
                      <a href={r.proof_url} target="_blank" rel="noreferrer" className="text-primary underline">
                        proof
                      </a>
                    </div>
                  )}
                  {r.notes && <div className="text-muted-foreground">{r.notes}</div>}
                </td>
                <td className="px-3 py-2">
                  <StatusBadge status={r.status} />
                  {r.rejection_reason && (
                    <div className="mt-1 text-xs text-muted-foreground">{r.rejection_reason}</div>
                  )}
                </td>
                <td className="px-3 py-2 text-right">
                  {r.status === "pending" ? (
                    <div className="flex justify-end gap-1">
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={busy === r.id}
                        onClick={() => {
                          setRejectFor(r);
                          setReason("");
                        }}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                      <Button size="sm" disabled={busy === r.id} onClick={() => act(r.id, "approve")}>
                        <Check className="mr-1 h-4 w-4" /> Approve
                      </Button>
                    </div>
                  ) : (
                    <span className="text-xs text-muted-foreground">
                      {r.reviewed_at ? new Date(r.reviewed_at).toLocaleDateString() : ""}
                    </span>
                  )}
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr>
                <td colSpan={8} className="px-3 py-10 text-center text-muted-foreground">
                  <Inbox className="mx-auto mb-2 h-6 w-6" />
                  {loading ? "Loading…" : rows.length === 0 ? "No top-up requests yet." : "No requests match filters."}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </CardContent>
    </Card>

      <Dialog open={!!rejectFor} onOpenChange={(o) => !o && setRejectFor(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject top-up request</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <Label>Reason (optional, shown to user)</Label>
            <Textarea value={reason} onChange={(e) => setReason(e.target.value)} />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setRejectFor(null)}>Cancel</Button>
            <Button
              variant="destructive"
              onClick={() => {
                if (!rejectFor) return;
                act(rejectFor.id, "reject", reason.trim() || undefined);
                setRejectFor(null);
              }}
            >
              Reject
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatusBadge({ status }: { status: TopupRequest["status"] }) {
  if (status === "approved") return <Badge className="bg-emerald-600 hover:bg-emerald-600"><Check className="mr-1 h-3 w-3" />Approved</Badge>;
  if (status === "rejected") return <Badge variant="destructive"><X className="mr-1 h-3 w-3" />Rejected</Badge>;
  if (status === "cancelled") return <Badge variant="secondary">Cancelled</Badge>;
  return <Badge variant="outline"><Clock className="mr-1 h-3 w-3" />Pending</Badge>;
}

function MethodEditor({
  value,
  onClose,
  onSave,
  saving,
}: {
  value: Partial<TopupMethod> | null;
  onClose: () => void;
  onSave: (v: Partial<TopupMethod>) => void;
  saving: boolean;
}) {
  const [v, setV] = useState<Partial<TopupMethod>>({});
  const open = !!value;
  if (open && v !== value && v.id !== value?.id) {
    // initialize once when dialog opens for a new target
    setV(value ?? {});
  }
  const set = <K extends keyof TopupMethod>(k: K, val: TopupMethod[K]) => setV((p) => ({ ...p, [k]: val }));

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{value?.id ? "Edit method" : "New top-up method"}</DialogTitle>
        </DialogHeader>
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="grid gap-1.5">
            <Label>Name</Label>
            <Input value={v.name ?? ""} onChange={(e) => set("name", e.target.value)} />
          </div>
          <div className="grid gap-1.5">
            <Label>Kind</Label>
            <Select value={v.kind ?? "bank"} onValueChange={(val) => set("kind", val as MethodKind)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {(Object.keys(KIND_META) as MethodKind[]).map((k) => (
                  <SelectItem key={k} value={k}>{KIND_META[k].label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid gap-1.5">
            <Label>Currency</Label>
            <Input value={v.currency ?? "USD"} onChange={(e) => set("currency", e.target.value.toUpperCase())} />
          </div>
          <div className="grid gap-1.5">
            <Label>Sort order</Label>
            <Input
              type="number"
              value={v.sort_order ?? 0}
              onChange={(e) => set("sort_order", parseInt(e.target.value || "0", 10))}
            />
          </div>
          <div className="grid gap-1.5">
            <Label>Min amount (cents)</Label>
            <Input
              type="number"
              value={v.min_amount_cents ?? 100}
              onChange={(e) => set("min_amount_cents", parseInt(e.target.value || "0", 10))}
            />
          </div>
          <div className="grid gap-1.5">
            <Label>Max amount (cents)</Label>
            <Input
              type="number"
              value={v.max_amount_cents ?? 1_000_000}
              onChange={(e) => set("max_amount_cents", parseInt(e.target.value || "0", 10))}
            />
          </div>
          <div className="grid gap-1.5">
            <Label>Fee percent</Label>
            <Input
              type="number"
              step="0.01"
              value={Number(v.fee_percent ?? 0)}
              onChange={(e) => set("fee_percent", parseFloat(e.target.value || "0"))}
            />
          </div>
          <div className="grid gap-1.5">
            <Label>Fee flat (cents)</Label>
            <Input
              type="number"
              value={v.fee_flat_cents ?? 0}
              onChange={(e) => set("fee_flat_cents", parseInt(e.target.value || "0", 10))}
            />
          </div>
          <div className="grid gap-1.5 sm:col-span-2">
            <Label>Instructions shown to user</Label>
            <Textarea
              rows={4}
              value={v.instructions ?? ""}
              onChange={(e) => set("instructions", e.target.value)}
              placeholder="E.g. Send to IBAN XXXX… and paste the reference below."
            />
          </div>
          <div className="grid gap-1.5 sm:col-span-2">
            <Label>Account details (JSON)</Label>
            <Textarea
              rows={3}
              value={JSON.stringify(v.account_details ?? {}, null, 2)}
              onChange={(e) => {
                try {
                  set("account_details", JSON.parse(e.target.value || "{}"));
                } catch {
                  /* ignore parse errors mid-typing */
                }
              }}
              className="font-mono text-xs"
            />
          </div>
          <div className="flex items-center justify-between rounded-md border p-3 sm:col-span-2">
            <div>
              <div className="text-sm font-medium">Auto-credit on webhook</div>
              <div className="text-xs text-muted-foreground">
                For card/crypto gateways that confirm payment via webhook.
              </div>
            </div>
            <Switch checked={v.auto_credit ?? false} onCheckedChange={(c) => set("auto_credit", c)} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={() => onSave(v)} disabled={saving}>
            {saving ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
